import type { GameStateManager } from '../game/GameState';
import type { GameData, GameSpeed } from '../game/types';

export class CompendiumModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  private currentPage: number = 0;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    } else if (event.code === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      this.previousPage();
    } else if (event.code === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.nextPage();
    }
  }

  show(): void {
    this.currentPage = 0;
    this.render();
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  private render(): void {
    const properties = this.gameData.propertiesHelper;
    if (properties.length === 0) return;

    const property = properties[this.currentPage];
    const totalPages = properties.length;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content compendium-modal" tabindex="0">
        <div class="compendium-fixed-header">
          <div class="modal-header">
            <h2>Compendium</h2>
            <button class="modal-close" data-action="close">×</button>
          </div>
          <div class="compendium-property-header">
            <span class="compendium-property-symbol" style="color: ${property.color}; font-size: 64px;">${property.symbol}</span>
            <h3 class="compendium-property-name">${property.name}</h3>
          </div>
        </div>
        <div class="compendium-scrollable-content">
          ${property.description ? `
          <div class="compendium-property-description">
            ${property.description}
          </div>
          ` : ''}
          
          <div class="compendium-levels">
            ${property.levels.map((level) => {
              const shouldHideContainment = level.level === 3 && 
                ['Lethal', 'Memetic', 'Antiepistemic', 'Hyperreal', 'Dormant'].includes(property.name) &&
                (!level.containment_effects || level.containment_effects.trim() === '' || level.containment_effects === 'No effect during containment.');
              
              return `
              <div class="compendium-level">
                <h4 class="compendium-level-title">Level ${level.level}</h4>
                <div class="compendium-level-content">
                  <div class="compendium-effect-section">
                    <h5>Research Effects</h5>
                    <p>${level.research_effects}</p>
                  </div>
                  ${!shouldHideContainment && level.containment_effects ? `
                  <div class="compendium-effect-section">
                    <h5>Containment Effects</h5>
                    <p>${level.containment_effects}</p>
                  </div>
                  ` : ''}
                </div>
              </div>
            `;
            }).join('')}
          </div>
        </div>
        <div class="modal-footer compendium-footer">
          <div class="compendium-navigation">
            <button class="compendium-nav-button" ${this.currentPage === 0 ? 'disabled' : ''} data-action="previous">← Previous</button>
            <span class="compendium-page-indicator">${this.currentPage + 1} / ${totalPages}</span>
            <button class="compendium-nav-button" ${this.currentPage === totalPages - 1 ? 'disabled' : ''} data-action="next">Next →</button>
          </div>
          <button class="button-confirm" data-action="close">Close</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }

    const previousButton = this.container.querySelector('[data-action="previous"]') as HTMLElement;
    if (previousButton) {
      previousButton.addEventListener('click', () => this.previousPage());
    }

    const nextButton = this.container.querySelector('[data-action="next"]') as HTMLElement;
    if (nextButton) {
      nextButton.addEventListener('click', () => this.nextPage());
    }
  }

  private previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.render();
    }
  }

  private nextPage(): void {
    const totalPages = this.gameData.propertiesHelper.length;
    if (this.currentPage < totalPages - 1) {
      this.currentPage++;
      this.render();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }
}
